import { Box, Flex, createStylesContext } from '@chakra-ui/react'
import { useMaskito } from '@maskito/react'
import type { ReactNode } from 'react'
import { Ref, RefCallback, useEffect, useImperativeHandle, useRef } from 'react'

import { BaseInputProps } from '.'

export const [BaseInputStylesProvider, useBaseInputStyles] =
  createStylesContext('BaseInput')

export const InputContainer = ({
  children,
  inputFieldRef,
  isInvalid,
  disabled,
  isTextarea,
  ...props
}: any) => {
  const styles = useBaseInputStyles()
  const containerSx = isTextarea
    ? {
        ...styles.inputContainer,
        alignItems: 'flex-start',
        overflow: 'visible',
      }
    : styles.inputContainer

  return (
    <Flex
      sx={containerSx}
      {...props}
      data-part="inputContainer"
      data-invalid={isInvalid}
      disabled={disabled}
      onClick={() => {
        if (!disabled) {
          inputFieldRef?.current?.focus()
        }
      }}
    >
      {children}
    </Flex>
  )
}

interface InputSideElementProps {
  children: ReactNode
  side: 'left' | 'right'
}

export const InputSideElement = ({ children, side }: InputSideElementProps) => (
  <Box
    flexShrink={0}
    display="flex"
    alignItems="center"
    {...(side === 'right' ? { marginLeft: 'auto' } : {})}
  >
    {children}
  </Box>
)

export const InputField = ({
  maskOptions,
  autoFocus,
  isInvalid,
  ref,
  ...props
}: BaseInputProps & { ref?: Ref<HTMLInputElement> }) => {
  const styles = useBaseInputStyles()
  const maskitoRef = useMaskito({
    options: maskOptions,
  }) as RefCallback<HTMLInputElement>

  const internalRef = useRef<HTMLInputElement>(null)

  useImperativeHandle(maskitoRef, () => internalRef.current!)
  useImperativeHandle(ref, () => internalRef.current!)

  useEffect(() => {
    if (autoFocus) {
      setTimeout(() => {
        internalRef.current?.focus()
      }, 0)
    }
  }, [autoFocus, maskitoRef])

  const isTextarea = props.as === 'textarea'
  const fieldSx = isTextarea
    ? {
        ...styles.field,
        height: '14px',
        minHeight: '14px',
        lineHeight: '1.4',
        overflow: 'auto',
        resize: (props as any).resize ?? 'vertical',
        '&::-webkit-resizer': {
          display: 'block',
        },
      }
    : styles.field

  return (
    <Box
      as="input"
      type="text"
      ref={internalRef}
      sx={fieldSx}
      aria-invalid={isInvalid}
      {...props}
    />
  )
}
